import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { Vacancy, VacancyStep, AutomationTemplate, UserRole, Client } from '../types';
import { supabase_update, supabase_get_templates, supabase_get_clients, supabase } from '../services/supabaseService';
import { appendVacancyFlowStepHistory, type VacancyFlowActor } from '../utils/vacancyFlowHistory';
import { describeVacancySnapshotChanges } from '../utils/vacancyChangeDiff';
import {
  appendIntakeVersionHistory,
  buildPendingIntakeSnapshot,
  intakeBaselineSnapshotFromVacancy,
  intakeSnapshotToFormData,
  intakeSnapshotsEqual,
  lastIntakeHistorySnapshot,
  mergeIntakeSnapshotIntoVacancy,
  pickIntakeSnapshotFromUpdates,
  snapshotToVacancySnapshotForDiff,
  formDataFromVacancy,
  type VacancyIntakeSnapshot,
  type VacancyIntakeVersionEntry,
} from '../utils/vacancyIntakeVersions';
import { intakeSnapshotTextDiffSummaries, mergeStructuralAndTextDiffLines } from '../utils/intakeTextDiff';
import { isClientPortalHostname } from '../utils/isClientPortalHost';
import { Briefcase, MapPin, Euro, Sparkles, Loader2, Save, Check, LayoutTemplate, Plus, X, Wand2, Building2, FileDown, Users, Upload, Settings, TrendingUp, Search, ChevronDown, History } from 'lucide-react';
import IntakeVersionHistoryModal from './IntakeVersionHistoryModal';
import AiRichTextEditor from './AiRichTextEditor';
import ClientDetailModal from './ClientDetailModal';
import { generateVacancyDetails, gemini_analyze_tender, refineWebsiteText } from '../services/geminiService';
import DatePicker, { registerLocale } from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { nl } from 'date-fns/locale';

registerLocale('nl', nl);

const formatDateTimeString = (date: Date | null) => {
    if (!date) return '';
    return date.toISOString();
};

const parseDateString = (dateStr: string) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
};

const SearchableClientSelect = ({ 
    value, 
    onChange, 
    options, 
    placeholder, 
    disabled, 
    className, 
    allowEmpty = true,
    onCreateNew
}: { 
    value: string; 
    onChange: (val: string) => void; 
    options: Client[]; 
    placeholder: string; 
    disabled?: boolean; 
    className?: string; 
    allowEmpty?: boolean;
    onCreateNew?: (name: string) => void;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.id === value);
    const filteredOptions = options.filter(o => 
        o.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        o.address_city?.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl'));

    return (
        <div className="relative w-full text-slate-900" ref={wrapperRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full p-2.5 flex justify-between items-center bg-slate-50 border border-slate-100 rounded-lg text-left outline-none transition-all font-bold text-sm disabled:opacity-50 ${className}`}
            >
                <div className="truncate">
                    {selectedOption ? selectedOption.name : placeholder}
                </div>
                <ChevronDown size={14} className="text-slate-400 shrink-0" />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 z-50 w-full mt-1 bg-white border border-slate-200 shadow-xl rounded-xl overflow-hidden flex flex-col max-h-[300px]">
                    <div className="p-2 border-b border-slate-100 bg-slate-50 sticky top-0">
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                autoFocus
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Zoek op naam of stad..."
                                className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium outline-none focus:border-indigo-500 transition-colors"
                            />
                        </div>
                    </div>
                    <div className="overflow-y-auto no-scrollbar flex-1 bg-white p-1">
                        {allowEmpty && (
                            <div 
                                onClick={() => { onChange(''); setIsOpen(false); setSearchTerm(''); }}
                                className="px-3 py-2 hover:bg-slate-50 cursor-pointer rounded-lg mb-1 text-sm font-bold text-slate-500 flex items-center gap-2 transition-colors"
                            >
                                <X size={14} /> {placeholder}
                            </div>
                        )}
                        {filteredOptions.length === 0 ? (
                            <div className="p-4 text-center">
                                <p className="text-xs font-bold text-slate-400 mb-2">Geen bedrijven gevonden voor "{searchTerm}"</p>
                                <button
                                    onClick={() => {
                                        setIsOpen(false);
                                        if (onCreateNew) onCreateNew(searchTerm);
                                    }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg text-[11px] font-bold hover:bg-indigo-100 transition-colors"
                                >
                                    <Plus size={12} /> Nieuw bedrijf / werkplek aanmaken
                                </button>
                            </div>
                        ) : (
                            filteredOptions.map(client => (
                                <div 
                                    key={client.id}
                                    onClick={() => { onChange(client.id); setIsOpen(false); setSearchTerm(''); }}
                                    className={`px-3 py-2 hover:bg-indigo-50 cursor-pointer rounded-lg mb-0.5 transition-colors flex flex-col ${value === client.id ? 'bg-indigo-50 border border-indigo-100' : ''}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className={`text-sm font-bold ${value === client.id ? 'text-indigo-700' : 'text-slate-900'} truncate`}>{client.name}</span>
                                        {value === client.id && <Check size={14} className="text-indigo-600 shrink-0" />}
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-0.5 text-[10px] font-medium text-slate-500">
                                        {client.address_city ? (
                                            <span className="flex items-center gap-1"><MapPin size={10} /> {client.address_street || ''} {client.address_city}</span>
                                        ) : (
                                            <span className="italic">Geen adresgegevens</span>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export interface VacancyIntakeHandle {
    saveIfDirty: () => Promise<boolean>;
}

interface VacancyIntakeProps {
    vacancy: Vacancy;
    onUpdate: (updates: Partial<Vacancy>) => void;
    onNext: () => void;
    isReadOnly?: boolean;
    userRole?: UserRole;
    /** For Activiteit / audit trail on intake save */
    actorUser?: { id?: string; name?: string; email?: string };
}

const VacancyIntake = forwardRef<VacancyIntakeHandle, VacancyIntakeProps>(function VacancyIntake(
    { vacancy, onUpdate, onNext, isReadOnly = false, userRole, actorUser },
    ref
) {
    const [formData, setFormData] = useState(() => formDataFromVacancy(vacancy));

    const [clients, setClients] = useState<Client[]>([]);
    const [colleagues, setColleagues] = useState<any[]>([]);

    const [isSaving, setIsSaving] = useState(false);
    const [sections, setSections] = useState<AutomationTemplate[]>([]);
    const [showSectionModal, setShowSectionModal] = useState<string | null>(null); // Field name to insert into

    // AI Autofill State
    const [showAiModal, setShowAiModal] = useState(false);
    const [aiInstruction, setAiInstruction] = useState('');
    const [isAiGenerating, setIsAiGenerating] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [showNewClientModal, setShowNewClientModal] = useState(false);
    const [newClientPrefilledName, setNewClientPrefilledName] = useState('');

    // Tender Import State
    const [showTenderModal, setShowTenderModal] = useState(false);
    const [showTemplatePickerIntake, setShowTemplatePickerIntake] = useState(false);
    const [intakeTemplateList, setIntakeTemplateList] = useState<any[]>([]);
    const [tenderText, setTenderText] = useState('');
    const [tenderFile, setTenderFile] = useState<File | null>(null);
    const [isTenderAnalyzing, setIsTenderAnalyzing] = useState(false);

    const [showIntakeVersions, setShowIntakeVersions] = useState(false);

    const lastIntakeCheckpointRef = useRef<VacancyIntakeSnapshot | null>(null);
    const isDirtyRef = useRef(false);
    const formDataRef = useRef(formData);
    const handleSaveRef = useRef<any>(null);

    const intakeVersionsChronological = useMemo(() => {
        const h = (vacancy.data as any)?.intakeVersionHistory;
        if (!Array.isArray(h)) return [] as VacancyIntakeVersionEntry[];
        return [...h] as VacancyIntakeVersionEntry[];
    }, [vacancy.id, JSON.stringify((vacancy.data as any)?.intakeVersionHistory || [])]);

    const intakeVersionsNewestFirst = useMemo(
        () => [...intakeVersionsChronological].reverse(),
        [intakeVersionsChronological]
    );

    const [prevOpdrachtgeverId, setPrevOpdrachtgeverId] = useState(vacancy.data?.opdrachtgever_id);
    const [prevEindklantId, setPrevEindklantId] = useState(vacancy.eindklant_id);

    /** Nieuwe vacature: formulier opnieuw laden, checkpoint resetten. */
    useEffect(() => {
        lastIntakeCheckpointRef.current = null;
        isDirtyRef.current = false;
        setIsDirty(false);
        setFormData(formDataFromVacancy(vacancy));
        setPrevOpdrachtgeverId(vacancy.data?.opdrachtgever_id);
        setPrevEindklantId(vacancy.eindklant_id);
    }, [vacancy.id]);

    useEffect(() => {
        isDirtyRef.current = isDirty;
    }, [isDirty]);
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    const pendingIntakeDiffSnap = useMemo(
        () => snapshotToVacancySnapshotForDiff(buildPendingIntakeSnapshot(formData, vacancy, clients)),
        [formData, vacancy, clients]
    );

    /** Externe updates (bv. Builder-sync) — niet overschrijven terwijl gebruiker typt in Intake. */
    useEffect(() => {
        if (isDirtyRef.current) return;
        setFormData(formDataFromVacancy(vacancy));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        vacancy.id,
        vacancy.title,
        (vacancy.data as any)?.vacancyText,
        JSON.stringify(vacancy.shared_with_users),
        JSON.stringify(vacancy.shared_with_clients),
        JSON.stringify(vacancy.data?.builtVacancySections),
    ]);

    // Prefill Organization Text when client or eindklant changes or loads
    useEffect(() => {
        if (clients.length > 0) {
            const activeClientId = formData.eindklant_id || formData.opdrachtgever_id;
            const changed = (formData.opdrachtgever_id !== prevOpdrachtgeverId) || (formData.eindklant_id !== prevEindklantId);

            if (activeClientId && (changed || !formData.organizationText)) {
                const client = clients.find(c => c.id === activeClientId);
                if (client?.organizationDescription) {
                    setFormData(prev => ({ ...prev, organizationText: client.organizationDescription }));
                } else if (changed) {
                    // Clear out the previous organization's profile if the newly selected one is empty
                    setFormData(prev => ({ ...prev, organizationText: '' }));
                }
            }
            
            setPrevOpdrachtgeverId(formData.opdrachtgever_id);
            setPrevEindklantId(formData.eindklant_id);
        }
    }, [formData.opdrachtgever_id, formData.eindklant_id, clients]);

    useEffect(() => {
        const loadSections = async () => {
            const templates = await supabase_get_templates();
            setSections(templates.filter(t => t.type === 'Section'));
        };
        loadSections();

        supabase_get_clients().then(data => setClients(data || []));
    }, [userRole]);

    useEffect(() => {
        const fetchColleagues = async () => {
            let query = supabase.from('profiles').select('id, name, email, role');
            if (formData.clientId) {
                query = query.eq('client_id', formData.clientId);
            }
            const { data } = await query;
            if (data) setColleagues(data);
        };
        fetchColleagues();
    }, [formData.clientId]);

    // Warn on browser close/navigate when there are unsaved changes
    useEffect(() => {
        if (!isDirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    // Only sync to parent on explicit save (not on every keystroke)
    // The auto-sync below is intentionally removed to prevent half-filled vacatures.

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const handleTeamConfigChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, werfplaatsTeamConfig: { ...prev.werfplaatsTeamConfig, [field]: value } }));
    };

    const handleSalaryChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, salary: { ...prev.salary, [field]: value } }));
    };

    const handleInsertSection = (content: string) => {
        if (!showSectionModal) return;
        const currentContent = (formData as any)[showSectionModal] || '';
        handleChange(showSectionModal, currentContent + (currentContent ? '<br/><br/>' : '') + content);
        setShowSectionModal(null);
    };

    const handleAiAutofill = async () => {
        if (!formData.title) {
            alert("Vul eerst een functietitel in.");
            return;
        }
        setIsAiGenerating(true);
        try {
            let clientPrompts: Record<string, string> = {};
            try {
                const { supabase_get_client_settings } = await import('../services/supabaseService');
                const settings = await supabase_get_client_settings(vacancy.clientId);
                if (settings?.ai_prompts_settings) clientPrompts = settings.ai_prompts_settings;
            } catch(e) {}

            const details = await generateVacancyDetails(formData.title, aiInstruction, clientPrompts);

            setFormData(prev => ({
                ...prev,
                jobCode: prev.jobCode || details.jobCode,
                workplaceType: prev.workplaceType || details.workplaceType,
                location: prev.location || details.location,
                description: details.description || prev.description,
                requirements: details.requirements || prev.requirements,
                benefits: details.benefits || prev.benefits,
                experience: details.experience || prev.experience,
                education: details.education || prev.education,
                employmentType: details.employmentType || prev.employmentType,
                salary: {
                    ...prev.salary,
                    min: details.salary?.min || prev.salary.min,
                    max: details.salary?.max || prev.salary.max,
                    currency: details.salary?.currency || prev.salary.currency,
                    period: details.salary?.period || prev.salary.period
                }
            }));
            setShowAiModal(false);
            setAiInstruction('');
        } catch (e) {
            console.error("AI Generation failed:", e);
            alert("Froukje kon de gegevens niet genereren. Probeer het opnieuw.");
        } finally {
            setIsAiGenerating(false);
        }
    };

    const handleTenderImport = async () => {
        if (!tenderText.trim()) {
            alert("Plak eerst de tekst van de aanvraag/tender.");
            return;
        }
        setIsTenderAnalyzing(true);
        try {
            const result = await gemini_analyze_tender(tenderText);
            if (result) {
                setFormData(prev => ({
                    ...prev,
                    title: result.title || prev.title,
                    description: `<p><strong>AI Samenvatting:</strong><br/>${result.summary || result.description || ''}</p><hr/><br/><p><strong>Originele Tekst:</strong><br/><br/>${tenderText.split('\n').join('<br/>')}</p>`,
                    requirements: result.requirements ? result.requirements.join('\n- ') : (result.responsibilities ? result.responsibilities.join('\n- ') : prev.requirements),
                    planningDate: result.planningDate || prev.planningDate,
                    closingDate: result.closingDate || prev.closingDate,
                    startDate: result.startDate || prev.startDate,
                    contractType: result.contractType || prev.contractType,
                    contractDuration: result.contractDuration || prev.contractDuration
                }));
                setShowTenderModal(false);
                setTenderText('');
                setTenderFile(null);
            } else {
                alert("Fout bij het analyseren. Probeer opnieuw.");
            }
        } catch (e) {
            console.error("Tender analyze failed", e);
            alert("Er is iets misgegaan tijdens het analyseren.");
        } finally {
            setIsTenderAnalyzing(false);
        }
    };

    const handleTenderFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setTenderFile(file);
        setIsTenderAnalyzing(true);

        try {
            let textToAnalyze = '';

            // Check if it's a PDF and we have pdfjsLib available (loaded globally in index.html)
            if (file.type === 'application/pdf' && (window as any).pdfjsLib) {
                const arrayBuffer = await file.arrayBuffer();
                const pdf = await (window as any).pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                let fullText = '';
                for (let i = 1; i <= pdf.numPages; i++) {
                    const page = await pdf.getPage(i);
                    const textContent = await page.getTextContent();
                    const pageText = textContent.items.map((item: any) => item.str).join(' ');
                    fullText += pageText + '\n';
                }
                textToAnalyze = fullText;
            } else {
                // If not PDF or no pdfjsLib, fallback to base64. 
                // But note: large files might cause 405s if base64 size exceeds limits.
                const reader = new FileReader();
                textToAnalyze = await new Promise((resolve) => {
                    reader.onload = (e) => {
                        const base64Str = e.target?.result as string;
                        resolve(base64Str.split(',')[1] || base64Str);
                    };
                    reader.readAsDataURL(file);
                });
            }

            // If we extracted text, pass it as string, otherwise pass base64 object
            const payload = typeof textToAnalyze === 'string' && textToAnalyze.length > 100
                ? textToAnalyze // Send raw text (fast, small payload)
                : { base64: textToAnalyze, mimeType: file.type }; // Fallback for images/docx (larger payload)

            const result = await gemini_analyze_tender(payload);

            if (result) {
                // Sla document op in vacancy files and database (Background task)
                if (file && vacancy?.id) {
                    import('../services/supabaseService').then(async ({ supabase_upload_file, supabase }) => {
                        try {
                            const url = await supabase_upload_file(file, 'documents', 'vacancy-files/');
                            if (url) {
                                const newFile = {
                                    id: Date.now().toString(),
                                    name: file.name,
                                    url: url,
                                    contentType: file.type,
                                    dateAdded: new Date().toISOString(),
                                    source: 'tender'
                                };
                                const updatedFiles = [...(vacancy.data?.files || []), newFile];
                                
                                const activeClientId = formData.clientId || vacancy.clientId || formData.opdrachtgever_id;
                                if (activeClientId) {
                                    await supabase.from('documents').insert([{
                                        title: file.name,
                                        storage_path: url,
                                        file_type: file.name.split('.').pop() || 'unknown',
                                        vacancy_id: (vacancy.id && vacancy.id.length === 36) ? vacancy.id : null,
                                        client_id: activeClientId
                                    }]);
                                }

                                // Update in DB and local state
                                await supabase.from('vacancies').update({ data: { ...vacancy.data, files: updatedFiles } }).eq('id', vacancy.id);
                                onUpdate({ data: { ...vacancy.data, files: updatedFiles } });
                            }
                        } catch (e) {
                            console.error("Tender document autosave failed:", e);
                        }
                    });
                }

                setFormData(prev => ({
                    ...prev,
                    title: result.title || prev.title,
                    description: `<p><strong>AI Samenvatting:</strong><br/>${result.summary || result.description || ''}</p><hr/><br/><p><strong>Originele Tekst uit document:</strong><br/><br/>${typeof payload === 'string' ? payload.split('\n').join('<br/>') : 'Zie geüpload document.'}</p>`,
                    requirements: result.requirements ? result.requirements.join('\n- ') : (result.responsibilities ? result.responsibilities.join('\n- ') : prev.requirements),
                    planningDate: result.planningDate || prev.planningDate,
                    closingDate: result.closingDate || prev.closingDate,
                    startDate: result.startDate || prev.startDate,
                    contractType: result.contractType || prev.contractType,
                    contractDuration: result.contractDuration || prev.contractDuration
                }));
                setShowTenderModal(false);
                setTenderText('');
                setTenderFile(null);
            } else {
                alert("Fout bij het analyseren van het document. Probeer opnieuw.");
            }
        } catch (err) {
            console.error("Document analyze failed", err);
            alert("Er is iets misgegaan tijdens het uploaden/analyseren.");
        } finally {
            setIsTenderAnalyzing(false);
        }
    };

    const handleSave = async (proceed: boolean = false, formOverride?: typeof formData): Promise<boolean> => {
        if (isReadOnly) return false;

        const f = formOverride ?? formData;
        if (formOverride) setFormData(formOverride);

        if (!f.opdrachtgever_id) {
            alert("Klant (Opdrachtgever) is verplicht. Selecteer of maak eerst een klant aan.");
            return false;
        }

        if (!f.location?.trim()) {
            alert("Locatie / Standplaats is verplicht (belangrijk voor talentpools op afstand).");
            return false;
        }

        setIsSaving(true);
        const hadUserEdits = isDirty;
        try {
            const combinedDescription = `
${f.organizationText ? `<h2>Over de organisatie</h2><p>${f.organizationText}</p>` : ''}
${f.description}
        `.trim();

            // Prepare updates
            const updates: any = {
                title: f.title,
                status: (vacancy.status || 'Concept') as any,
                data: {
                    ...vacancy.data,
                    jobCode: f.jobCode,
                    workplaceType: f.workplaceType as any,
                    location: f.location,
                    salary: f.salary,
                    experience: f.experience,
                    education: f.education,
                    benefits: f.benefits,
                    organizationText: f.organizationText,
                    category: f.employmentType,
                    suggestions: f.requirements,
                    hoursPerWeek: f.hoursPerWeek,
                    startDate: f.startDate,
                    planningDate: f.planningDate,
                    closingDate: f.closingDate,
                    contractDuration: f.contractDuration,
                    contractType: f.contractType,
                    placementTypes: f.placementTypes,
                    clientName: clients.find((c) => c.id === f.opdrachtgever_id)?.name || vacancy.data?.clientName,
                    opdrachtgeverLogoUrl: clients.find((c) => c.id === f.opdrachtgever_id)?.logo_url || vacancy.data?.opdrachtgeverLogoUrl,
                },
                clientId: f.clientId,
                opdrachtgever_id: f.opdrachtgever_id || null,
                eindklant_id: f.eindklant_id || null,
                shared_with_clients: f.sharedWithClients,
                shared_with_users: f.sharedWithUsers,
            };

            updates.data.vacancyText = f.description;

            const existingBuilt = Array.isArray(vacancy.data?.builtVacancySections)
                ? vacancy.data!.builtVacancySections
                : [];
            if (existingBuilt.length > 0) {
                const builtHTML = existingBuilt
                    .map((s: { title: string; content: string }) => `<h3>${s.title}</h3>${s.content}`)
                    .join('\n\n');
                updates.data.builtVacancySections = existingBuilt;
                updates.description = builtHTML;
                updates.data.finalVacancyContent = builtHTML;
            } else {
                updates.description = combinedDescription;
                updates.data.finalVacancyContent = combinedDescription;
            }

            let actor: VacancyFlowActor | undefined = actorUser
                ? { ...actorUser, role: userRole ? String(userRole) : undefined }
                : undefined;
            if (!actor?.id && !actor?.name) {
                try {
                    const {
                        data: { user },
                    } = await supabase.auth.getUser();
                    if (user) {
                        actor = {
                            id: user.id,
                            name: (user.user_metadata?.full_name as string) || user.email || 'Gebruiker',
                            email: user.email || undefined,
                            role: userRole ? String(userRole) : undefined,
                        };
                    }
                } catch {
                    /* ignore */
                }
            }
            if (lastIntakeCheckpointRef.current === null) {
                lastIntakeCheckpointRef.current =
                    lastIntakeHistorySnapshot(vacancy.data as Record<string, unknown> | undefined) ??
                    intakeBaselineSnapshotFromVacancy(vacancy as any);
            }
            const prevBaseline = lastIntakeCheckpointRef.current;
            const intakeSnap = pickIntakeSnapshotFromUpdates(updates);
            const hadContentDelta = !intakeSnapshotsEqual(prevBaseline, intakeSnap);
            const shouldRecordCheckpoint = hadContentDelta || hadUserEdits;

            if (shouldRecordCheckpoint) {
                const prevForDiff = snapshotToVacancySnapshotForDiff(prevBaseline);
                const nextForDiff = snapshotToVacancySnapshotForDiff(intakeSnap);
                const textLines = intakeSnapshotTextDiffSummaries(prevBaseline, intakeSnap);
                const structural = describeVacancySnapshotChanges(prevForDiff, nextForDiff);
                let summaries = mergeStructuralAndTextDiffLines(textLines, structural);
                if (summaries.length === 0) {
                    summaries = [
                        hadUserEdits && !hadContentDelta
                            ? 'Opgeslagen na bewerking (zelfde inhoud volgens automatische diff t.o.v. vorige checkpoint).'
                            : 'Opgeslagen — wijziging gedetecteerd (geen detailregels).',
                    ];
                }
                if (summaries.length > 0) {
                    updates.data = appendVacancyFlowStepHistory(updates.data as Record<string, unknown>, {
                        kind: 'intake_save',
                        stepId: String(VacancyStep.INTAKE),
                        stepLabel: '1. Intake',
                        changeSummaries: summaries,
                    }, actor) as typeof updates.data;
                }
                updates.data = appendIntakeVersionHistory(
                    updates.data as Record<string, unknown>,
                    intakeSnap,
                    summaries,
                    actor
                ) as typeof updates.data;
                lastIntakeCheckpointRef.current = intakeSnap;
            }

            onUpdate(updates);

            try {
                const dbUpdates = { ...updates } as any;
                if ('clientId' in dbUpdates) {
                    dbUpdates.client_id = dbUpdates.clientId || (vacancy as any).client_id;
                    delete dbUpdates.clientId;
                }

                const savePromise = supabase_update(vacancy.id, dbUpdates);
                const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 2000));

                await Promise.race([savePromise, timeoutPromise]);
            } catch (e) {
                console.error('Save error (continued optimistically):', e);
            }
            return true;
        } finally {
            setIsSaving(false);
            if (proceed) {
                setIsDirty(false);
                onNext();
            } else {
                setIsDirty(false);
            }
        }
    };

    useEffect(() => {
        handleSaveRef.current = handleSave;
    });

    useImperativeHandle(ref, () => ({
        saveIfDirty: async () => {
            if (isReadOnly) return true;
            if (!isDirtyRef.current) return true;
            return (await handleSaveRef.current?.(false, formDataRef.current)) ?? false;
        },
    }), [isReadOnly]);

    const restoreIntakeVersionToForm = (entry: VacancyIntakeVersionEntry) => {
        const fd = intakeSnapshotToFormData(entry.snapshot);
        setFormData(fd);
        lastIntakeCheckpointRef.current = entry.snapshot;
        const mergedData = mergeIntakeSnapshotIntoVacancy(vacancy, entry.snapshot);
        onUpdate({
            title: entry.snapshot.title,
            description: entry.snapshot.description,
            status: entry.snapshot.status as any,
            clientId: entry.snapshot.clientId,
            opdrachtgever_id: entry.snapshot.opdrachtgever_id,
            eindklant_id: entry.snapshot.eindklant_id,
            shared_with_users: entry.snapshot.shared_with_users,
            shared_with_clients: entry.snapshot.shared_with_clients,
            data: mergedData,
        } as any);
        setIsDirty(true);
        setShowIntakeVersions(false);
    };

    const renderSectionButton = (field: string) => (
        <button
            onClick={() => setShowSectionModal(field)}
            className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#e51b23] hover:text-[#B71C1C] transition-colors mb-2"
        >
            <Plus size={12} /> Standaard Sectie Toevoegen
        </button>
    );

    return (
        <>
        <div className="max-w-5xl mx-auto space-y-8 animate-fadeIn pb-20 relative">
            {/* Header / Actions - Compact */}
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight truncate">
                        {formData.title || 'Nieuwe Vacature'} 
                        {formData.opdrachtgever_id && (() => {
                            const c = clients.find(c => c.id === formData.opdrachtgever_id);
                            return c ? ` - ${c.name}` : '';
                        })()}
                        {formData.location ? ` - ${formData.location}` : ''}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-slate-500 font-medium">Intake &amp; Details</p>
                        {isDirty && (
                            <span className="flex items-center gap-1.5 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200/60 px-2 py-0.5 rounded-md">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block" />
                                Niet opgeslagen
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 lg:gap-2.5 justify-end">
                    {!isClientPortalHostname() && (
                    <button
                        type="button"
                        onClick={() => setShowIntakeVersions(true)}
                        className="bg-slate-50 text-slate-700 px-3 py-1.5 rounded-md font-semibold text-[11px] hover:bg-slate-100 transition-colors border border-slate-200 flex items-center gap-1.5 shrink-0"
                    >
                        <History size={14} />
                        Geschiedenis
                        {intakeVersionsChronological.length > 0 && (
                            <span className="min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-slate-200 text-[10px] font-black flex items-center justify-center">
                                {intakeVersionsChronological.length}
                            </span>
                        )}
                    </button>
                    )}
                {!isReadOnly && (
                    <>
                        <button
                            onClick={() => setShowTenderModal(true)}
                            className="bg-amber-50 text-amber-700 px-3 py-1.5 rounded-md font-semibold text-[11px] hover:bg-amber-100 transition-colors border border-amber-200 flex items-center gap-1.5 shrink-0"
                        >
                            <FileDown size={14} /> Tender Aanvraag
                        </button>
                        <button
                            onClick={async () => {
                                const { supabase } = await import('../services/supabaseService');
                                const { data: tmpl } = await supabase
                                    .from('vacancy_templates')
                                    .select('id, name, content')
                                    .order('created_at', { ascending: false });
                                setIntakeTemplateList(tmpl || []);
                                setShowTemplatePickerIntake(true);
                            }}
                            className="bg-indigo-50 text-indigo-600 px-3 py-1.5 rounded-md font-semibold text-[11px] hover:bg-indigo-100 transition-colors border border-indigo-200 flex items-center gap-1.5 shrink-0"
                        >
                            <LayoutTemplate size={14} /> Open Sjabloon
                        </button>
                        <button
                            onClick={() => setShowAiModal(true)}
                            className="bg-purple-50 text-purple-600 px-3 py-1.5 rounded-md font-semibold text-[11px] hover:bg-purple-100 transition-colors border border-purple-200 flex items-center gap-1.5 shrink-0"
                        >
                            <Sparkles size={14} /> Vraag Froukje
                        </button>
                        <div className="hidden lg:block w-px h-5 bg-slate-200 mx-0.5 shrink-0" />
                        <button
                            onClick={() => handleSave(false)}
                            disabled={isSaving}
                            className="bg-white text-slate-700 border border-slate-200 px-4 py-1.5 rounded-md font-bold text-[11px] hover:bg-slate-50 transition-all flex items-center gap-1.5 shrink-0 active:scale-95"
                        >
                            <Save size={14} /> Opslaan
                        </button>
                        <button
                            onClick={() => handleSave(true)}
                            disabled={isSaving}
                            className="bg-[#e51b23] text-white px-4 py-1.5 rounded-md font-bold text-[11px] shadow-sm hover:bg-[#B71C1C] transition-all flex items-center gap-1.5 shrink-0 active:scale-95"
                        >
                            <Check size={14} /> Opslaan & Verder
                        </button>
                    </>
                )}
                </div>
            </div>

            <div className="flex flex-col gap-6">
                <div className="w-full space-y-6 animate-fadeIn">
                    {/* Core Data */}
                    {/* Section 1: Job Title & Context */}
                    <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                        <h2 className="text-xs font-black text-slate-900 mb-4 flex items-center gap-2 uppercase tracking-widest">
                            <Briefcase className="text-slate-400" size={14} /> Context
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between items-center mb-1.5">
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide">Functietitel <span className="text-red-500">*</span></label>
                                </div>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    placeholder="bv. Senior Marketing Manager"
                                    disabled={isReadOnly}
                                    className="w-full p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-slate-900 focus:ring-1 focus:ring-[#e51b23] outline-none transition-all font-bold text-sm mb-4"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                                        Locatie / Standplaats <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.location || ''}
                                        onChange={(e) => handleChange('location', e.target.value)}
                                        placeholder="bv. Amsterdam (of Hybride)"
                                        disabled={isReadOnly}
                                        className="w-full p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-slate-900 focus:ring-1 focus:ring-[#e51b23] outline-none transition-all font-bold text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                                        Opleidingsniveau
                                    </label>
                                    <select
                                        value={formData.education || ''}
                                        onChange={(e) => handleChange('education', e.target.value)}
                                        disabled={isReadOnly}
                                        className="w-full p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-slate-900 focus:ring-1 focus:ring-[#e51b23] outline-none transition-all font-bold text-sm appearance-none"
                                    >
                                        <option value="">Selecteer niveau...</option>
                                        <option value="Niet van toepassing">Niet van toepassing</option>
                                        <option value="MBO">MBO</option>
                                        <option value="HBO">HBO</option>
                                        <option value="Bachelor">Bachelor</option>
                                        <option value="Master">Master</option>
                                        <option value="Gepromoveerd">Gepromoveerd</option>
                                    </select>
                                </div>
                            </div>

                            <div className={`grid grid-cols-1 ${formData.opdrachtgever_id ? 'md:grid-cols-2' : ''} gap-4`}>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                                        Klant
                                    </label>
                                    <SearchableClientSelect
                                        value={formData.opdrachtgever_id}
                                        onChange={(val) => {
                                            const prevOpdrachtgever = formData.opdrachtgever_id;
                                            handleChange('opdrachtgever_id', val);
                                            if (!val) {
                                                handleChange('eindklant_id', '');
                                            } else if (!formData.eindklant_id || formData.eindklant_id === prevOpdrachtgever) {
                                                // Auto-sync werkplek to same client unless user already picked a different one
                                                handleChange('eindklant_id', val);
                                            }
                                        }}
                                        options={clients}
                                        placeholder="Eigen Organisatie of Klant"
                                        disabled={isReadOnly}
                                        className="bg-slate-50 border border-slate-100 focus:ring-1 focus:ring-[#e51b23]"
                                        allowEmpty={true}
                                        onCreateNew={(name) => { setShowNewClientModal(true); setNewClientPrefilledName(name); }}
                                    />
                                </div>
                                {formData.opdrachtgever_id && (
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center justify-between">
                                            <span className="flex items-center gap-1.5">
                                                Werkplek <span className="text-slate-400 normal-case tracking-normal font-medium">(kan afwijken van klant)</span>
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setShowNewClientModal(true)}
                                                className="flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-200 text-slate-600 rounded-md text-[9px] font-bold uppercase tracking-wide hover:bg-slate-100 transition-colors"
                                            >
                                                <Plus size={9} /> Nieuw bedrijf
                                            </button>
                                        </label>
                                        <SearchableClientSelect
                                            value={formData.eindklant_id || ''}
                                            onChange={(val) => handleChange('eindklant_id', val)}
                                            options={clients}
                                            placeholder="Zelfde als klant"
                                            disabled={isReadOnly}
                                            className="bg-slate-50 border border-slate-100 focus:ring-1 focus:ring-[#e51b23]"
                                            allowEmpty={true}
                                            onCreateNew={(name) => { setShowNewClientModal(true); setNewClientPrefilledName(name); }}
                                        />
                                    </div>
                                )}
                            </div>








                        </div>
                    </section>

                    {/* Section 2: Details & Voorwaarden */}
                    <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 mt-6">
                        <h2 className="text-xs font-black text-slate-900 mb-4 flex items-center gap-2 uppercase tracking-widest">
                            <Briefcase className="text-slate-400" size={14} /> Details & Voorwaarden
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Werkvorm</label>
                                <select
                                    value={formData.workplaceType}
                                    onChange={(e) => handleChange('workplaceType', e.target.value)}
                                    disabled={isReadOnly}
                                    className="w-full p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-slate-900 focus:ring-1 focus:ring-[#e51b23] outline-none transition-all font-bold text-sm appearance-none"
                                >
                                    <option value="On-site">On-site</option>
                                    <option value="Hybrid">Hybride</option>
                                    <option value="Remote">Volledig Remote</option>
                                </select>
                            </div>
                            
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Dienstverband</label>
                                <select
                                    value={formData.employmentType}
                                    onChange={(e) => handleChange('employmentType', e.target.value)}
                                    disabled={isReadOnly}
                                    className="w-full p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-slate-900 focus:ring-1 focus:ring-[#e51b23] outline-none transition-all font-bold text-sm appearance-none"
                                >
                                    <option value="Fulltime">Fulltime</option>
                                    <option value="Parttime">Parttime</option>
                                    <option value="Freelance/ZZP">Freelance / ZZP</option>
                                    <option value="Stage">Stage</option>
                                  </select>
                              </div>

                              <div>
                                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Aantal uren per week</label>
                                  <select
                                      value={formData.hoursPerWeek}
                                      onChange={(e) => handleChange('hoursPerWeek', e.target.value)}
                                      disabled={isReadOnly}
                                      className="w-full p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-slate-900 focus:ring-1 focus:ring-[#e51b23] outline-none transition-all font-bold text-sm appearance-none"
                                  >
                                      <option value="In overleg">In overleg</option>
                                      <option value="16">16</option>
                                      <option value="24">24</option>
                                      <option value="28">28</option>
                                      <option value="32">32</option>
                                      <option value="36">36</option>
                                      <option value="40">40</option>
                                      <option value="Aangepast">Aangepast</option>
                                  </select>
                              </div>

                            <div className="md:col-span-2">
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Plaatsingsvorm</label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {['Vaste dienst', 'ZZP', 'IP model'].map((type) => (
                                        <label key={type} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 border border-transparent hover:border-slate-100 rounded-lg transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={formData.placementTypes?.includes(type)}
                                                onChange={(e) => {
                                                    const updated = e.target.checked
                                                        ? [...(formData.placementTypes || []), type]
                                                        : (formData.placementTypes || []).filter((t: string) => t !== type);
                                                    handleChange('placementTypes', updated);
                                                }}
                                                disabled={isReadOnly}
                                                className="w-4 h-4 rounded border-slate-200 text-[#e51b23] focus:ring-[#e51b23] disabled:opacity-50"
                                            />
                                            <span className="text-[11px] font-bold text-slate-700">{type}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Start Werving</label>
                                <DatePicker
                                    locale="nl"
                                    dateFormat="d MMMM yyyy HH:mm"
                                    timeFormat="HH:mm"
                                    showTimeSelect
                                    selected={parseDateString(formData.planningDate || formData.startDate)}
                                    onChange={(date: Date | null) => handleChange('planningDate', formatDateTimeString(date))}
                                    disabled={isReadOnly}
                                    placeholderText="Kies een startdatum en tijd"
                                    className="w-full p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-slate-900 focus:ring-1 focus:ring-[#e51b23] outline-none transition-all font-bold text-sm cursor-pointer"
                                    wrapperClassName="w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Sluitingsdatum (deadline)</label>
                                <DatePicker
                                    locale="nl"
                                    dateFormat="d MMMM yyyy HH:mm"
                                    timeFormat="HH:mm"
                                    showTimeSelect
                                    selected={parseDateString(formData.closingDate)}
                                    onChange={(date: Date | null) => handleChange('closingDate', formatDateTimeString(date))}
                                    disabled={isReadOnly}
                                    placeholderText="Kies een deadline"
                                    className="w-full p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-slate-900 focus:ring-1 focus:ring-[#e51b23] outline-none transition-all font-bold text-sm cursor-pointer"
                                    wrapperClassName="w-full"
                                />
                            </div>


                            
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Contract Duur</label>
                                <input
                                    type="text"
                                    value={formData.contractDuration}
                                    onChange={(e) => handleChange('contractDuration', e.target.value)}
                                    placeholder="bv. 12 maanden"
                                    disabled={isReadOnly}
                                    className="w-full p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-slate-900 focus:ring-1 focus:ring-[#e51b23] outline-none transition-all font-bold text-sm"
                                />
                            </div>
                        </div>
                    </section>


                </div>

                {/* Free Text */}
                <div className="w-full">
                    <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 h-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xs font-black text-slate-900 flex items-center gap-2 uppercase tracking-widest">
                                <Sparkles className="text-slate-400" size={14} /> Inhoud & Details
                            </h2>
                            <div className="flex gap-2">
                                {/* Buttons could go here */}
                            </div>
                        </div>

                        <div className="flex flex-col gap-6 h-full">
                            <div className="space-y-4">
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide">Originele Vacaturetekst / Briefing</label>
                                        <button onClick={() => setShowSectionModal('description')} className="text-[10px] font-bold text-[#e51b23] hover:underline flex items-center gap-1"><Plus size={10} /> Sectie</button>
                                    </div>
                                    <div className="border border-slate-100 rounded-lg overflow-hidden">
                                        <AiRichTextEditor
                                            hideAiFeatures={true}
                                            value={formData.description}
                                            onChange={(val) => handleChange('description', val)}
                                            placeholder="Plak hier de volledige originele vacaturetekst of briefing..."
                                            minHeight="600px"
                                            standardSections={sections}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* SECTIONS MODAL */}
            {showSectionModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[80vh]">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">Kies Standaard Sectie</h3>
                            <button onClick={() => setShowSectionModal(null)} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X size={20} className="text-slate-400" /></button>
                        </div>
                        <div className="p-6 overflow-y-auto space-y-3 custom-scrollbar">
                            {sections.length === 0 ? (
                                <div className="text-center py-10 text-slate-400">
                                    <LayoutTemplate size={40} className="mx-auto mb-4 opacity-50" />
                                    <p className="text-sm font-bold">Geen secties gevonden.</p>
                                    <p className="text-xs">Maak templates aan in de Automation Engine.</p>
                                </div>
                            ) : (
                                sections.map(section => (
                                    <button
                                        key={section.id}
                                        onClick={() => handleInsertSection(section.content || '')}
                                        className="w-full text-left p-4 rounded-xl border border-slate-100 hover:border-[#e51b23] hover:bg-red-50 transition-all group group-hover:shadow-md"
                                    >
                                        <h4 className="font-bold text-slate-900 text-sm group-hover:text-[#e51b23]">{section.name}</h4>
                                        <p className="text-xs text-slate-400 mt-1 truncate">{section.content?.replace(/<[^>]*>/g, '')}</p>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* AI AUTOFILL MODAL */}
            {showAiModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-purple-50 to-indigo-50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-purple-600">
                                    <Wand2 size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black uppercase tracking-tight text-purple-900">Froukje Auto-Fill</h3>
                                    <p className="text-xs text-purple-700 font-medium">Laat AI je vacature invullen</p>
                                </div>
                            </div>
                            <button onClick={() => !isAiGenerating && setShowAiModal(false)} className="p-2 hover:bg-white/50 rounded-full transition-colors"><X size={20} className="text-purple-400" /></button>
                        </div>

                        <div className="p-8 space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Functietitel (Basis voor AI)</label>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900">
                                    {formData.title || "Nog geen titel ingevuld..."}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Extra Instructies (Optioneel)</label>
                                <textarea
                                    value={aiInstruction}
                                    onChange={(e) => setAiInstruction(e.target.value)}
                                    placeholder="bv. Focus op junior talent, we zitten in Rotterdam, we bieden een leaseauto..."
                                    className="w-full p-4 bg-white border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-purple-500 outline-none transition-all font-medium min-h-[100px]"
                                />
                                <p className="text-[10px] text-slate-400 mt-2">Geef Froukje wat context om de resultaten te verbeteren.</p>
                            </div>

                            <button
                                onClick={handleAiAutofill}
                                disabled={isAiGenerating || !formData.title}
                                className="w-full py-4 rounded-xl bg-purple-600 text-white font-black uppercase tracking-widest hover:bg-purple-700 transition-all shadow-lg hover:shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isAiGenerating ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Froukje is aan het schrijven...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={16} />
                                        Genereer Vacature
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TENDER IMPORT MODAL */}
            {showTenderModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-amber-50 to-orange-50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-amber-600">
                                    <FileDown size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black uppercase tracking-tight text-amber-900">Aanvraag/Tender Importeren</h3>
                                    <p className="text-xs text-amber-700 font-medium">Plak de tekst en laat de AI het formulier invullen</p>
                                </div>
                            </div>
                            <button onClick={() => !isTenderAnalyzing && setShowTenderModal(false)} className="p-2 hover:bg-white/50 rounded-full transition-colors"><X size={20} className="text-amber-400" /></button>
                        </div>

                        <div className="p-8 space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Tender / Aanvraag Tekst</label>
                                <textarea
                                    value={tenderText}
                                    onChange={(e) => setTenderText(e.target.value)}
                                    placeholder="Plak hier de tekstuele opdracht, verantwoordelijkheden en eisen..."
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all outline-none mb-4"
                                    rows={8}
                                    disabled={isTenderAnalyzing || tenderFile !== null}
                                />

                                <div className="relative border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:bg-slate-50 transition-colors">
                                    <input
                                        type="file"
                                        accept=".pdf,.doc,.docx"
                                        onChange={handleTenderFileUpload}
                                        disabled={isTenderAnalyzing}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                    />
                                    <div className="flex flex-col items-center justify-center gap-2 pointer-events-none">
                                        {isTenderAnalyzing && tenderFile ? (
                                            <Loader2 size={24} className="text-amber-500 animate-spin" />
                                        ) : (
                                            <Upload size={24} className="text-slate-400" />
                                        )}
                                        {tenderFile ? (
                                            <span className="text-sm font-bold text-amber-600">{tenderFile.name} (Verwerken...)</span>
                                        ) : (
                                            <>
                                                <span className="text-sm font-bold text-slate-700">OF Sleep een PDF of DOCX bestand hier</span>
                                                <span className="text-xs text-slate-500">Max 10MB</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleTenderImport}
                                disabled={isTenderAnalyzing || (!tenderText.trim() && !tenderFile)}
                                className="w-full py-4 rounded-xl text-white font-black uppercase tracking-widest text-xs shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50
                                    bg-[#e51b23] hover:bg-black active:scale-[0.98]"
                            >
                                {isTenderAnalyzing ? (
                                    <><Loader2 size={16} className="animate-spin" /> Controleren en verwerken...</>
                                ) : (
                                    <><Wand2 size={16} /> Analyseer & Vul In</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

        {/* INLINE NIEUW KLANT/WERKPLEK MODAL - schuift van rechts in */}
        {showNewClientModal && (
            <ClientDetailModal
                client={null}
                initialName={newClientPrefilledName}
                accessProfiles={[]}
                currentUserRole={userRole || null}
                currentUserClientId={vacancy.clientId || null}
                onClose={() => { setShowNewClientModal(false); setNewClientPrefilledName(''); }}
                onSaveSuccess={async (newClient?: any) => {
                    setShowNewClientModal(false);
                    setNewClientPrefilledName('');
                    // Ververs de klantenlijst zodat het nieuwe bedrijf direct selecteerbaar is
                    const updated = await supabase_get_clients(vacancy.clientId);
                    setClients(updated);
                    // Auto-select the newly created client
                    if (newClient?.id) {
                        handleChange('opdrachtgever_id', newClient.id);
                    }
                }}
            />
        )}

        {/* INTAKE SJABLOON PICKER MODAL */}
        {showTemplatePickerIntake && (
            <div className="fixed inset-0 z-[600] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowTemplatePickerIntake(false)}>
                <div className="bg-white w-full max-w-md mx-4 rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Open Sjabloon</h3>
                            <p className="text-[11px] text-slate-400 mt-0.5">Selecteer een sjabloon om mee te starten</p>
                        </div>
                        <button onClick={() => setShowTemplatePickerIntake(false)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 transition-colors text-lg leading-none">&times;</button>
                    </div>
                    <div className="p-4 max-h-80 overflow-y-auto">
                        {intakeTemplateList.length === 0 ? (
                            <div className="text-center py-10 text-slate-400 text-sm">Geen sjablonen gevonden</div>
                        ) : (
                            <div className="space-y-2">
                                {intakeTemplateList.map((t: any) => (
                                    <button
                                        key={t.id}
                                        onClick={() => {
                                            if (t.content && Array.isArray(t.content)) {
                                                const combined = t.content.map((s: any) => '<h3>' + s.title + '</h3>' + s.content).join('\n\n');
                                                onUpdate({ description: combined });
                                            }
                                            setShowTemplatePickerIntake(false);
                                        }}
                                        className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-left transition-all group"
                                    >
                                        <div>
                                            <p className="text-sm font-bold text-slate-900 group-hover:text-indigo-700">{t.name}</p>
                                            <p className="text-[10px] text-slate-400">{Array.isArray(t.content) ? t.content.length + ' secties' : ''}</p>
                                        </div>
                                        <span className="text-slate-300 group-hover:text-indigo-500 text-lg">&#8594;</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}

        <IntakeVersionHistoryModal
            open={showIntakeVersions}
            onClose={() => setShowIntakeVersions(false)}
            versionsNewestFirst={intakeVersionsNewestFirst}
            pendingDiffSnap={pendingIntakeDiffSnap}
            isReadOnly={isReadOnly}
            onRestoreInForm={isReadOnly ? undefined : restoreIntakeVersionToForm}
            onRestoreAndSave={
                isReadOnly
                    ? undefined
                    : (entry: VacancyIntakeVersionEntry) => {
                          if (
                              !window.confirm(
                                  'Deze versie wordt teruggezet en direct opgeslagen. Doorgaan?'
                              )
                          )
                              return;
                          const fd = intakeSnapshotToFormData(entry.snapshot);
                          setShowIntakeVersions(false);
                          void handleSave(false, fd);
                      }
            }
        />

        </>
    );
});

export default VacancyIntake;
